import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useParams, useNavigate, NavLink, Link } from "react-router-dom";
import { Header } from "./components/Header";
import { ShimmerTable } from "react-shimmer-effects";
import toast, { Toaster } from "react-hot-toast";
import { type } from "@testing-library/user-event/dist/type";

const API_URL = "/api";

export default function ScoringGuide() {
  const { id } = useParams();
  console.log("🔎 Route id:", id);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [company, setCompany] = useState(null);
  const [transactionData, setTransactionData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checklistFormData, setChecklistFormData] = useState([]);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const response = await fetch(`${API_URL}/getTransactionDetails/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        console.log("📥 Full API Response:", data);
        setTransactionData(data);
        console.log("transactionData:", transactionData);
        // setFormData(data?.DocumentCheckList || {});
        setFormData(data); // keep full object for Loan Info
        setCompany(data);
        console.log("Data:", data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyDetails();
  }, []);

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setFormData(transactionData);
    setChecklistFormData(transactionData?.DocumentCheckList || []);
    setIsEditing(false);
  };

  const handleInputChange = (sectionKey, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const { _id, ...rest } = formData;
    if (!_id) {
      alert("Missing _id");
      return;
    }
    // Remove any fields with undefined values
    const cleanedData = {};
    for (const key in rest) {
      if (rest[key] !== undefined) {
        cleanedData[key] = rest[key];
      }
    }
    const updatePayload = {
      _id: _id, // Make sure it's a string
      update_data: cleanedData,
    };

    try {
      const response = await fetch(
        "http://localhost:8000/riverchain/techop/v1.1/updateCompanyLog",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatePayload),
        }
      );

      const resultText = await response.text();

      if (!response.ok) {
        console.error("❌ Update failed:", resultText);
        alert("Update failed:\n" + resultText);
      } else {
        toast.success("Company log updated successfully");
      }
    } catch (error) {
      console.error("🚨 Network error:", error);
      toast.danger("An error occurred while updating the profile.");
    } finally {
      setIsSubmitting(false);
    }
  };
  // Utility: format date values into DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date)) return dateString; // fallback if invalid date
    return date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
  };
  const today = new Date();
  const formatted = today.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long", // 👈 gives full month name
    year: "numeric",
  });
  const sections = [
    {
      title: "Borrower Score",
      data: [
        { name: "Borrower: A", points: 20 },
        { name: "Borrower: B", points: 15 },
        { name: "Borrower: C", points: 0 },
        { name: "Borrower: D", points: -10 },
      ],
    },
    {
      title: "Contract Counterparty Score",
      data: [
        { name: "Counterparty: A", points: 20 },
        { name: "Counterparty: B", points: 15 },
        { name: "Counterparty: C", points: 0 },
        { name: "Counterparty: D", points: -10 },
      ],
    },
    {
      title: "Project (Contract)",
      criteria: [
        {
          title: "Duration (Term)",
          options: [
            { label: "Less than 3 months (90 days)", points: 10 },
            { label: "Between 3 months and 6 months", points: 5 },
            { label: "Between 6 months and 12 months", points: 0 },
            { label: "Longer than 12 months", points: -10 },
          ],
        },
        {
          title: "Work Complexity",
          options: [
            { label: "Standard construction works", points: 5 },
            { label: "Complex projects", points: 0 },
          ],
        },
        {
          title: "Involvement of expert",
          options: [
            { label: "With third-party consultant", points: 5 },
            { label: "No third-party consultant", points: 0 },
          ],
        },
        {
          title: "Supply risk",
          options: [
            { label: "No known supply risks", points: 0 },
            { label: "Known supply/price volatility", points: -5 },
          ],
        },
        {
          title: "Evidence of project progress",
          options: [
            { label: "Construction in progress", points: 10 },
            { label: "Not started", points: 0 },
          ],
        },
        {
          title: "Terms",
          options: [
            { label: "Standard terms", points: 0 },
            { label: "Non-standard terms", points: -5 },
            { label: "Unknown terms", points: -5 },
          ],
        },
        {
          title: "Contract history of delays/accidents",
          options: [
            { label: "No known history", points: 0 },
            { label: "Known history", points: -10 },
          ],
        },
      ],
    },
    {
      title: "Project (Employer)",
      criteria: [
        {
          title: "Relationship with River chain",
          options: [
            { label: "Existing relationship with Riverchain ", points: 5 },
            { label: "No prior relationship with Riverchain ", points: 0 },
          ],
        },
        {
          title: "Reputation",
          options: [
            { label: "Positive market reputation", points: 5 },
            { label: "Neutral market reputation", points: 0 },
            { label: "Negative market reputation", points: -5 },
          ],
        },
        {
          title:
            "Financial rating / Credit rating (from Third-party Credit Rating Agency)",
          options: [
            { label: "A and B", points: 5 },
            { label: "C", points: 0 },
            { label: "Below C", points: -5 },
            { label: "N/A", points: 0 },
          ],
        },
      ],
    },
  ];
  const transactionSections = [
    {
      title: "Loan Structure",
      criteria: [
        {
          title: "Effect of income alteration on security",
          options: [
            { label: "0% to 10%", points: 0 },
            { label: "11% to 20%", points: 5 },
            { label: "21% to 30%", points: 8 },
            { label: "> 30%", points: 10 },
          ],
        },
        {
          title: "Lifetime of the Project / Term of the loan",
          options: [
            { label: "< 1.0", points: -10 },
            { label: "1.0 to 1.1", points: 0 },
            { label: "1.2 to 1.5", points: 5 },
            { label: "> 1.5", points: 10 },
          ],
        },
      ],
    },
    {
      title: "Security",
      criteria: [
        {
          title: "Collection account",
          options: [
            {
              label: "Borrower will use Riverchain collection account",
              points: 10,
            },
            {
              label: "Borrower will not use Riverchain collection account",
              points: -5,
            },
          ],
        },
        {
          title: "Type of Security",
          options: [
            { label: "Unsecured", points: -10 },
            { label: "Receivables", points: 0 },
            { label: "Receivables and Other Assets or Guarantees", points: 10 },
          ],
        },
        {
          title: "Months of low Loan/NEP/term",
          options: [
            { label: "0", points: 10 },
            { label: "1 to 3", points: 0 },
            { label: "> 3", points: -10 },
          ],
        },
      ],
    },
    {
      title: "Concentration",
      criteria: [
        {
          title: "Exposure to Borrower",
          options: [
            { label: "Exposure to Borrower is < 10% of Portfolio ", points: 0 },
            {
              label: "Exposure to Borrower is > 10% of Portfolio ",
              points: -10,
            },
          ],
        },
        {
          title: "Exposure to Project",
          options: [
            { label: "Exposure to Borrower is < 10% of Portfolio ", points: 0 },
            {
              label: "Exposure to Borrower is > 10% of Portfolio ",
              points: -10,
            },
          ],
        },
        {
          title: "Exposure to Counterparty",
          options: [
            {
              label: "Exposure to the Counterparty is < 25% of Portfolio",
              points: 0,
            },
            {
              label: "Exposure to the Counterparty is > 25% of Portfolio",
              points: -5,
            },
          ],
        },
        {
          title: "Exposure to Employer",
          options: [
            {
              label: "Exposure to the Employer is < 50% of Portfolio",
              points: 0,
            },
            {
              label: "Exposure to the Employer is > 50% of Portfolio",
              points: -5,
            },
          ],
        },
      ],
    },
  ];

  // -------------------- Render Logic --------------------

  let totalMax = 0;
  let totalMin = 0;

  const renderRows = sections.flatMap((section, sectionIndex) => {
    // Handle both data[] and criteria[]
    if (section.data) {
      const pointsList = section.data.map((r) => r.points);
      const max = Math.max(...pointsList);
      const min = Math.min(...pointsList);
      totalMax += max;
      totalMin += min;

      return [
        <tr key={`header-${sectionIndex}`} style={{ background: "#f0f0f0" }}>
          <td colSpan="7" style={{ fontWeight: "bold" }}>
            {section.title}
          </td>
        </tr>,
        ...section.data.map((r, i) => (
          <tr key={`${sectionIndex}-${i}`} style={{ textAlign: "center" }}>
            <td>{r.name}</td>
            <td>{r.points}</td>
            <td colSpan="3"></td>
            {i === 0 && (
              <>
                <td rowSpan={section.data.length} style={{ color: "green" }}>
                  {max}
                </td>
                <td rowSpan={section.data.length} style={{ color: "red" }}>
                  {min}
                </td>
              </>
            )}
          </tr>
        )),
      ];
    }

    if (section.criteria) {
      return [
        <tr key={`header-${sectionIndex}`} style={{ background: "#e9f2ff" }}>
          <td colSpan="7" style={{ fontWeight: "bold" }}>
            {section.title}
          </td>
        </tr>,
        ...section.criteria.flatMap((criterion, critIndex) => {
          const points = criterion.options.map((o) => o.points);
          const max = Math.max(...points);
          const min = Math.min(...points);
          totalMax += max;
          totalMin += min;

          return [
            <tr
              key={`crit-${sectionIndex}-${critIndex}`}
              style={{ fontWeight: "bold" }}
            >
              <td colSpan="7">{criterion.title}</td>
            </tr>,
            ...criterion.options.map((opt, optIndex) => (
              <tr
                key={`opt-${sectionIndex}-${critIndex}-${optIndex}`}
                style={{ textAlign: "center" }}
              >
                <td style={{ width: "500px" }}>{opt.label}</td>
                <td style={{ width: "200px" }}>{opt.points}</td>
                <td colSpan="3" style={{ width: "300px" }}></td>
                {optIndex === 0 && (
                  <>
                    <td
                      rowSpan={criterion.options.length}
                      style={{ color: "green" }}
                    >
                      {max}
                    </td>
                    <td
                      rowSpan={criterion.options.length}
                      style={{ color: "red" }}
                    >
                      {min}
                    </td>
                  </>
                )}
              </tr>
            )),
          ];
        }),
      ];
    }

    return null;
  });

  const footerRow = (
    <tr style={{ borderTop: "3px solid black", fontWeight: "bold" }}>
      <td colSpan="6" style={{ textAlign: "right" }}>
        Total Business Risk
      </td>
      <td style={{ color: "green" }}>{totalMax}</td>
      <td style={{ color: "red" }}>{totalMin}</td>
    </tr>
  );

  // -------------------- Transaction Risk --------------------

  let transactionMax = 0;
  let transactionMin = 0;

  const renderTransactionRows = transactionSections.flatMap(
    (section, sectionIndex) => [
      <tr key={`t-header-${sectionIndex}`} style={{ background: "#dfeaf5" }}>
        <td colSpan="7" style={{ fontWeight: "bold" }}>
          {section.title}
        </td>
      </tr>,
      ...section.criteria.flatMap((criterion, critIndex) => {
        const points = criterion.options.map((o) => o.points);
        const max = Math.max(...points);
        const min = Math.min(...points);
        transactionMax += max;
        transactionMin += min;

        return [
          <tr
            key={`t-crit-${sectionIndex}-${critIndex}`}
            style={{ fontWeight: "bold" }}
          >
            <td colSpan="7">{criterion.title}</td>
          </tr>,
          ...criterion.options.map((opt, optIndex) => (
            <tr
              key={`t-opt-${sectionIndex}-${critIndex}-${optIndex}`}
              style={{ textAlign: "center" }}
            >
              <td>{opt.label}</td>
              <td>{opt.points}</td>
              <td colSpan="3"></td>
              {optIndex === 0 && (
                <>
                  <td
                    rowSpan={criterion.options.length}
                    style={{ color: "green" }}
                  >
                    {max}
                  </td>
                  <td
                    rowSpan={criterion.options.length}
                    style={{ color: "red" }}
                  >
                    {min}
                  </td>
                </>
              )}
            </tr>
          )),
        ];
      }),
    ]
  );

  const transactionFooter = (
    <tr style={{ borderTop: "3px solid black", fontWeight: "bold" }}>
      <td colSpan="6" style={{ textAlign: "right" }}>
        Total Transaction Risk
      </td>
      <td style={{ color: "green" }}>{transactionMax}</td>
      <td style={{ color: "red" }}>{transactionMin}</td>
    </tr>
  );

  return (
    <div className="app-container">
      <Toaster />
      <div className="container-fluid">
        <main className="content">
          <Header />
        </main>
      </div>

      <div className="container">
        <div className="row mt-4">
          <div className="col-md-12">
            <NavLink to={`/Dashboard`} className="btn btn-dark ms-2">
              Back to Transaction List
            </NavLink>

            <br />
            <br />
            <NavLink
              to={`/ViewTransactionList/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Loan Details
            </NavLink>

            <NavLink
              to={`/Drawdowns/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Drawdowns
            </NavLink>

            <NavLink
              to={`/ApplicationChecklist/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Application Checklist
            </NavLink>

            <NavLink
              to={`/ScoreCard/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Scorecard
            </NavLink>

            <NavLink
              to={`/TransactionSummary/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Transaction Summary
            </NavLink>

            <NavLink
              to={`/TermSheet/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Term Sheet
            </NavLink>

            <NavLink
              to={`/Payments/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Payments
            </NavLink>
            <NavLink
              to={`/RepaymentAndInterest/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Repayment & Interest(Term)
            </NavLink>
            <NavLink
              to={`/ScoringCriteriaTf/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Scoring Criteria
            </NavLink>

            <NavLink
              to={`/ScoringGuide/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Scoring Guide
            </NavLink>
            <NavLink
              to={`/InterestCalculations/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Interest Calculations
            </NavLink>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 style={{ marginTop: "15px" }}>Scoring Criteria</h3>
              <div>
                {isEditing && (
                  <button
                    className="btn btn-secondary me-2 btn-sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                )}
                <button
                  className="btn btn-primary btn-sm"
                  onClick={async () => {
                    if (isEditing) {
                      setIsSubmitting(true);
                      await handleSubmit();
                      setIsSubmitting(false);
                    } else {
                      setFormData(transactionData);
                      setIsEditing(true);
                    }
                  }}
                  disabled={isSubmitting}
                >
                  {isEditing
                    ? isSubmitting
                      ? "Submitting..."
                      : "Submit"
                    : "Edit"}
                </button>
              </div>
            </div>

            {loading || !transactionData ? (
              <ShimmerTable row={5} col={5} />
            ) : (
              <>
                <div className="col-md-12">
                  <table>
                    <thead>
                      <tr
                        style={{
                          borderBlock: "2px solid black",
                          textAlign: "center",
                        }}
                      >
                        <th>Criteria</th>
                        <th>Points</th>
                        <th colSpan={"3"}>Notes & Definitions</th>
                      </tr>
                      <tr style={{ backgroundColor: "blue", color: "white" }}>
                        <th colSpan={"5"}>Business Risk Score</th>
                        <th>Max</th>
                        <th style={{ paddingLeft: "20px" }}>Min</th>
                      </tr>
                      <tr
                        style={{
                          borderBlock: "2px solid black",
                          textAlign: "center",
                        }}
                      >
                        <th>Criteria</th>
                        <th>Points</th>
                        <th colSpan={"3"}>Notes & Definitions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderRows}
                      {footerRow}
                    </tbody>
                  </table>
                  <br></br>

                  <table>
                    <thead>
                      <tr style={{ backgroundColor: "blue", color: "white" }}>
                        <th colSpan={"5"}>Transaction Risk Score</th>
                        <th>Max</th>
                        <th style={{ paddingLeft: "20px" }}>Min</th>
                      </tr>
                      <tr
                        style={{
                          borderBlock: "2px solid black",
                          textAlign: "center",
                        }}
                      >
                        <th>Criteria</th>
                        <th>Points</th>
                        <th colSpan={"3"}>Notes & Definitions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderTransactionRows}
                      {transactionFooter}
                    </tbody>
                  </table>
                </div>
                <br></br>
                <div className="col-md-12">
                  <table>
                    <thead>
                      <tr>
                        <th
                          style={{
                            paddingRight: "1050px",
                            backgroundColor: "lightblue",
                          }}
                        >
                          Combined Transaction Score
                        </th>
                      </tr>
                    </thead>
                  </table>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <table>
                      <thead>
                        <tr>
                          <th>Business Score</th>
                          <th>Category</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ width: "300px" }}>
                            Between 61 and 85 points
                          </td>
                          <td
                            style={{
                              backgroundColor: "green",
                              textAlign: "center",
                              width: "200px",
                            }}
                          >
                            A
                          </td>
                        </tr>
                        <tr>
                          <td>Between 31 and 60 points</td>
                          <td
                            style={{
                              backgroundColor: "blue",
                              textAlign: "center",
                            }}
                          >
                            B
                          </td>
                        </tr>
                        <tr>
                          <td>Between 0 and 30 points</td>
                          <td
                            style={{
                              backgroundColor: "yellow",
                              textAlign: "center",
                            }}
                          >
                            C
                          </td>
                        </tr>
                        <tr>
                          <td>Less than 0 points</td>
                          <td
                            style={{
                              backgroundColor: "lightblue",
                              textAlign: "center",
                            }}
                          >
                            D
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <table>
                      <thead>
                        <tr style={{ borderBlock: "2px solid black" }}>
                          <th>Business score/Transaction Score</th>
                          <th style={{ width: "100px", textAlign: "center" }}>
                            A
                          </th>
                          <th style={{ width: "100px", textAlign: "center" }}>
                            B
                          </th>
                          <th style={{ width: "100px", textAlign: "center" }}>
                            C
                          </th>
                          <th style={{ width: "100px", textAlign: "center" }}>
                            D
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>I</td>
                          <td
                            style={{
                              textAlign: "center",
                              backgroundColor: "#bbd5a5ff",
                            }}
                          >
                            Good
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              backgroundColor: "#bbd5a5ff",
                            }}
                          >
                            Good
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              backgroundColor: "#c6c082ff",
                            }}
                          >
                            Medium
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              backgroundColor: "#e83737ff",
                            }}
                          >
                            Poor
                          </td>
                        </tr>
                        <tr>
                          <td>II</td>
                          <td
                            style={{
                              textAlign: "center",
                              backgroundColor: "#bbd5a5ff",
                            }}
                          >
                            Good
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              backgroundColor: "#bbd5a5ff",
                            }}
                          >
                            Good
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              backgroundColor: "#c6c082ff",
                            }}
                          >
                            Medium
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              backgroundColor: "#e83737ff",
                            }}
                          >
                            Poor
                          </td>
                        </tr>
                        <tr>
                          <td>III</td>
                          <td
                            style={{
                              textAlign: "center",
                              backgroundColor: "#c6c082ff",
                            }}
                          >
                            Medium
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              backgroundColor: "#c6c082ff",
                            }}
                          >
                            Medium
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              backgroundColor: "#c6c082ff",
                            }}
                          >
                            Medium
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              backgroundColor: "#e83737ff",
                            }}
                          >
                            Poor
                          </td>
                        </tr>
                        <tr>
                          <td>IV</td>
                          <td
                            style={{
                              textAlign: "center",
                              backgroundColor: "#e83737ff",
                            }}
                          >
                            Poor
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              backgroundColor: "#e83737ff",
                            }}
                          >
                            Poor
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              backgroundColor: "#e83737ff",
                            }}
                          >
                            Poor
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              backgroundColor: "#e83737ff",
                            }}
                          >
                            Poor
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <br></br>
                <div className="row">
                  <div className="col-md-6">
                    <table>
                      <thead>
                        <tr>
                          <th>Transaction Score</th>
                          <th>Category</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ width: "300px" }}>
                            Between 30 and 50 points
                          </td>
                          <td
                            style={{
                              backgroundColor: "green",
                              textAlign: "center",
                              width: "200px",
                            }}
                          >
                            I
                          </td>
                        </tr>
                        <tr>
                          <td>Between 16 and 29 points </td>
                          <td
                            style={{
                              backgroundColor: "blue",
                              textAlign: "center",
                            }}
                          >
                            II
                          </td>
                        </tr>
                        <tr>
                          <td>Between 0 and 15 points</td>
                          <td
                            style={{
                              backgroundColor: "yellow",
                              textAlign: "center",
                            }}
                          >
                            III
                          </td>
                        </tr>
                        <tr>
                          <td>Less than 0 points</td>
                          <td
                            style={{
                              backgroundColor: "lightblue",
                              textAlign: "center",
                            }}
                          >
                            IV
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <table>
                      <today>
                        <tr>
                          <td>
                            Good Quality: transactions should be financed.
                          </td>
                        </tr>
                        <tr>
                          <td>
                            Medium Quality: should be financed with strong
                            security and extensive monitoring provisions.
                          </td>
                        </tr>
                        <tr>
                          <td>
                            Poor Quality: should only be financed with an
                            explicit decision by the CEO. Poor quality
                            transactions require strict monitoring and
                            restrictive covenants. If significant deterioration
                            of creditability is probable, the company must
                            require additional security and prepare a collection
                            action plan to minimize the potential for loss.
                          </td>
                        </tr>
                      </today>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
